import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { AuthService, UserDirectoryItem } from '../../../services/AuthService';

interface EditUserDialogProps {
  open: boolean;
  user: UserDirectoryItem | null;
  onClose: () => void;
  /** 변경 성공 시 호출 — 목록 재조회 트리거. */
  onUpdated: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_USERNAME = 3;
const MIN_PASSWORD = 8;

/**
 * 유저 정보 변경 다이얼로그 — admin 전용. 아이디/이메일/비밀번호를 변경한다.
 * 비밀번호는 비워두면 변경하지 않는다. 백엔드 PATCH /v0.1/auth/users/:id.
 */
const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, user, onClose, onUpdated }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUsername(user?.username ?? '');
    setEmail(user?.email ?? '');
    setPassword('');
    setError(null);
    setSubmitting(false);
    setShowPassword(false);
  }, [open, user]);

  const handleSubmit = async () => {
    if (!user) return;
    const nextUsername = username.trim();
    const nextEmail = email.trim();
    const usernameChanged = nextUsername !== user.username;
    const emailChanged = nextEmail !== user.email;
    const passwordChanged = password.length > 0;

    if (!usernameChanged && !emailChanged && !passwordChanged) {
      setError('변경할 아이디, 이메일 또는 비밀번호를 입력해 주세요.');
      return;
    }
    if (usernameChanged && nextUsername.length < MIN_USERNAME) {
      setError(`아이디는 최소 ${MIN_USERNAME}자 이상이어야 합니다.`);
      return;
    }
    if (emailChanged && !EMAIL_RE.test(nextEmail)) {
      setError('올바른 이메일 형식이 아닙니다.');
      return;
    }
    if (passwordChanged && password.length < MIN_PASSWORD) {
      setError(`비밀번호는 최소 ${MIN_PASSWORD}자 이상이어야 합니다.`);
      return;
    }

    const payload: { username?: string; email?: string; password?: string } = {};
    if (usernameChanged) payload.username = nextUsername;
    if (emailChanged) payload.email = nextEmail;
    if (passwordChanged) payload.password = password;

    setSubmitting(true);
    setError(null);
    try {
      await AuthService.updateUser(user.id, payload);
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || '유저 정보 변경 실패');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>유저 정보 변경</DialogTitle>
      <DialogContent dividers>
        {user && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>{user.name}</strong> 님의 아이디·이메일·비밀번호를 변경합니다.
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="아이디"
            fullWidth
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label="이메일"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
          />
          <TextField
            label="새 비밀번호"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={`비워두면 변경하지 않습니다 · 변경 시 최소 ${MIN_PASSWORD}자`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          취소
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? '변경 중...' : '변경'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
